/**
 * @deprecated
 * Please use Video component (packages/components/Video/Video.tsx)
 *
 * @TODO: Replace all the usage with the new Video component
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useDebouncedCallback, useRequestClass, useUI, useWindowResize} from 'coreHooks';

import Icon from 'brandComponents/Icon';

import {handleMobilePlayButton, resizeVideo} from './helpers';
import {IVideoProps, Provider} from './IVideo';

// eslint-disable-next-line no-restricted-imports -- legacy SCSS, CSS Modules migration pending
import './Video.scss';

const getVideoProviderUrl = (provider: Provider, id: string) => {
	const providersMap = {
		[Provider.VIMEO]: `https://player.vimeo.com/video/${id}`,
		[Provider.CLOUDFLARE_STREAM]: `https://iframe.videodelivery.net/${id}`,
	};

	return providersMap[provider];
};

const Video = ({provider = Provider.VIMEO, id, iframe, options, adaptToContainer = false}: IVideoProps) => {
	const [player, setPlayer] = useState(null);
	const [originalDimensions, setOriginalDimensions] = useState(null);
	const [isPlaying, setIsPlaying] = useState(!!options?.autoplay || !!options?.vimeo?.autoplay || false);
	const resizeHandler = useWindowResize(300, false);
	const target = useRef(null);
	const lib = useRef(null);
	const defaultOptions = {vimeo: {controls: false}, cloudflare: {controls: false}};
	const isServerUI = useUI('isServer');

	const isPlayAfterPauseRef = useRef<boolean>(false);
	const isExternalPlayButton = options?.externalIcons?.play && options?.externalIcons?.pause;

	let src = '';
	const debResizeVideo = useDebouncedCallback(
		(iframeContainer, iframe, originalDimensions, callback) =>
			resizeVideo(iframeContainer, iframe, originalDimensions).then(callback),
		200,
	);
	const {isCORSRequest} = useRequestClass();

	useEffect(() => {
		if (adaptToContainer && target.current.classList.contains('plyr') && player) {
			debResizeVideo(target.current, player.media, originalDimensions);
		}
	}, [resizeHandler, originalDimensions, player]);

	if (provider === Provider.VIMEO || provider === Provider.CLOUDFLARE_STREAM) {
		src = getVideoProviderUrl(provider, id);
	}

	let wrapperProp;

	if (iframe) {
		wrapperProp = {dangerouslySetInnerHTML: {__html: iframe}};
	} else {
		wrapperProp = {children: [<iframe key={src} src={src}></iframe>]};
	}

	const targetCb = useCallback((ref) => {
		target.current = ref;
		init();
	}, []);

	const libCb = useCallback((ref) => {
		lib.current = ref.default;
		init();
	}, []);

	useEffect(() => {
		if (!isCORSRequest) {
			import('plyr').then(libCb);
		}
	}, []);

	const getOriginalDimensions = (plyr) => {
		if (plyr.embed && plyr.isVimeo) {
			Promise.all([plyr.embed.getVideoWidth(), plyr.embed.getVideoHeight()]).then((dimensions) => {
				setOriginalDimensions({
					width: dimensions[0],
					height: dimensions[1],
				});
			});
		}
	};

	const handleResize = (container, player) => {
		player.restart();
		container.classList.add('plyr--started');
	};

	const init = () => {
		if (isServerUI) {
			return null;
		}

		// Plyr should be attached using a <script .../> for an external application
		// eslint-disable-next-line no-undef
		const plyrLibrary = isCORSRequest ? Plyr : lib.current;

		if (target.current && plyrLibrary) {
			const node = target.current;
			let plyr = new plyrLibrary(node, {...defaultOptions, ...options});

			if (options?.muted) {
				plyr.on('play', () => {
					plyr.muted = true;
				});
			}

			// A workaround for a known Plyr issue of video not paused on mobile
			// https://github.com/sampotts/plyr/issues/1772
			plyr.on('click', handleMobilePlayButton(plyr));

			setPlayer(plyr);

			node.addEventListener(
				'playing',
				() => {
					if (adaptToContainer && !isPlayAfterPauseRef.current) {
						getOriginalDimensions(plyr);
						debResizeVideo(node, plyr.media, originalDimensions, () => handleResize(node, plyr));
					} else {
						node.classList.add('plyr--started');
					}

					isPlayAfterPauseRef.current = false;
					setIsPlaying(true);
				},
				true,
			);
		}
	};

	const handleClick = (e) => {
		e.preventDefault();

		if (player.paused) {
			player.embed?.play();
		} else {
			player.embed?.pause();
			isPlayAfterPauseRef.current = true;
			setIsPlaying(false);
		}
	};

	return (
		<>
			<div ref={targetCb} className="plyr__video-embed" {...wrapperProp}></div>
			{!!isExternalPlayButton && (
				<button className="plyr__external-play" onClick={handleClick}>
					<Icon name={isPlaying ? options.externalIcons.pause : options.externalIcons.play} />
				</button>
			)}
		</>
	);
};

export default Video;
